/**
 * Markdown rendering for the Nora chatbot.
 *
 * Inspired by CC's per-token rendering approach (cc/utils/markdown.ts):
 * - Uses marked.use() with a custom renderer so each token type is handled explicitly
 * - Code blocks: syntax highlighting + copy button baked into the HTML output
 *   (no post-processing regex hacks needed)
 * - Tables: wrapped in a scrollable container
 * - configured once globally — safe to call renderMarkdown() from anywhere
 *
 * Unlike CC (which renders to ANSI/Ink for the terminal), we produce HTML.
 */
import { marked } from 'marked';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import yaml from 'highlight.js/lib/languages/yaml';
import bash from 'highlight.js/lib/languages/bash';
import json from 'highlight.js/lib/languages/json';
import typescript from 'highlight.js/lib/languages/typescript';
import go from 'highlight.js/lib/languages/go';
import java from 'highlight.js/lib/languages/java';
import cpp from 'highlight.js/lib/languages/cpp';
import csharp from 'highlight.js/lib/languages/csharp';
import php from 'highlight.js/lib/languages/php';
import ruby from 'highlight.js/lib/languages/ruby';
import sql from 'highlight.js/lib/languages/sql';
import xml from 'highlight.js/lib/languages/xml';
import mdLang from 'highlight.js/lib/languages/markdown';

// Register highlight.js languages — runs once at module load time
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('js', javascript);
hljs.registerLanguage('jsx', javascript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('py', python);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('yml', yaml);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('sh', bash);
hljs.registerLanguage('shell', bash);
hljs.registerLanguage('json', json);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('ts', typescript);
hljs.registerLanguage('tsx', typescript);
hljs.registerLanguage('go', go);
hljs.registerLanguage('java', java);
hljs.registerLanguage('cpp', cpp);
hljs.registerLanguage('c', cpp);
hljs.registerLanguage('csharp', csharp);
hljs.registerLanguage('cs', csharp);
hljs.registerLanguage('php', php);
hljs.registerLanguage('ruby', ruby);
hljs.registerLanguage('rb', ruby);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('markdown', mdLang);
hljs.registerLanguage('md', mdLang);

let configured = false;

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function configureMarked(): void {
  if (configured) return;
  configured = true;

  // copy SVG icons
  const copyIcon = `<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>`;

  marked.use({
    gfm: true,    // GitHub Flavored Markdown: tables, task lists, strikethrough
    breaks: false, // Keep false — breaks:true interferes with GFM table/list parsing

    renderer: {
      // Code blocks — like CC's formatToken 'code' case but for HTML output.
      // Builds the .nora-code-block structure directly, no regex post-processing.
      code(code: string, language: string | undefined): string {
        const lang = (language ?? '').split(/\s+/)[0].toLowerCase();
        let highlighted: string;
        try {
          if (lang && hljs.getLanguage(lang)) {
            highlighted = hljs.highlight(code, { language: lang, ignoreIllegals: true }).value;
          } else if (code.length < 8000) {
            highlighted = hljs.highlightAuto(code).value;
          } else {
            highlighted = escHtml(code);
          }
        } catch {
          highlighted = escHtml(code);
        }

        const langAttr = lang ? ` language-${escHtml(lang)}` : '';
        return (
          `<div class="nora-code-block">` +
          `<div class="nora-code-header">` +
          `<span class="nora-code-lang">${escHtml(lang)}</span>` +
          `<button class="nora-copy-btn" aria-label="Copy code">${copyIcon} <span>Copy</span></button>` +
          `</div>` +
          `<pre class="nora-pre${langAttr}"><code class="hljs${langAttr}">${highlighted}</code></pre>` +
          `</div>`
        );
      },

      // Tables — wrap in scrollable container so wide tables don't overflow.
      table(header: string, body: string): string {
        return (
          `<div class="nora-table-wrapper">` +
          `<table><thead>${header}</thead><tbody>${body}</tbody></table>` +
          `</div>`
        );
      },
    },
  });
}

/**
 * Normalize non-standard code fence styles to backtick fences that marked understands.
 *
 * Some LLMs emit colon-fenced blocks (:::lang\n...\n:::) — a directive syntax used
 * by Docusaurus/remark-directive that marked doesn't parse as code blocks.
 * Convert them here so syntax highlighting still works.
 */
function normalizeCodeFences(md: string): string {
  // Match :::word (or :::) on its own line, content, then ::: on its own line.
  // The `m` flag makes ^ match start-of-line so we don't accidentally match mid-line.
  return md.replace(/^:::([\w-]*)[^\n]*\n([\s\S]*?)\n^:::[ \t]*$/gm, (_match, lang, body) => {
    return '```' + lang + '\n' + body + '\n```';
  });
}

/**
 * Convert markdown text to HTML string.
 * Equivalent to CC's applyMarkdown() but produces HTML for the web UI.
 * Always synchronous — the renderer has no async operations.
 */
export function renderMarkdown(content: string): string {
  configureMarked();
  return marked.parse(normalizeCodeFences(content)) as string;
}
